/* eslint max-classes-per-file: 0 */

import { AxiosRequestConfig } from 'axios'
import { PermissionsGroupCollection } from '@/typings/api/PermissionsGroup'
import { SessionCollection } from '@/typings/api/Session'
import {
  User as Data,
  UserCollection as Collection,
  UserProfile,
  UserActionCollection,
  UserParticipantsCollection,
  UserContacts,
} from '@/typings/api/User'
import {
  BaseApiResponse as BR,
  BaseApiResponseCollection as BRC,
} from '@/typings/common'

import { BaseApiAuth } from '@/api/BaseApiAuth'


export class UsersApi extends BaseApiAuth<Data, Collection> {
  protected readonly endpoint = 'users';

  public updateSecurity(id: Data['id'], data: unknown, config: AxiosRequestConfig = {}) {
    const path = `${id}/update-security/`
    const [url] = this.getUrl(path)
    return this.request<never, BR<never>>('POST', url, { data, ...config })
  }

  public updateActive(data: unknown, config: AxiosRequestConfig = {}) {
    const path = 'update-active/'
    const [url] = this.getUrl(path)
    return this.request<never, BR<never>>('POST', url, { data, ...config })
  }

  public getGroups(id: Data['id'], params: unknown, config: AxiosRequestConfig = {}) {
    const path = `${id}/groups/`
    const [url] = this.getUrl(path)
    return this.request<never, BRC<PermissionsGroupCollection>>('GET', url, { params, ...config })
  }

  public getSessions(id: Data['id'], params: unknown, config: AxiosRequestConfig = {}) {
    const path = `${id}/sessions/`
    const [url] = this.getUrl(path)
    return this.request<never, BRC<SessionCollection>>('GET', url, { params, ...config })
  }

  public getCurrent(rawParams: Record<string, unknown> = {}, rawConfig: AxiosRequestConfig = {}) {
    const path = 'current/'
    const [url] = this.getUrl(path)
    const { redirect401, ...params } = rawParams
    const config = { params, ...rawConfig, redirect401 } as AxiosRequestConfig
    return this.request<never, BR<UserProfile>>('GET', url, config)
  }

  public updateCurrent(data: UserProfile, config: AxiosRequestConfig = {}) {
    const path = 'current/update/'
    const [url] = this.getUrl(path)
    return this.request<never, BR<never>>('POST', url, { data, ...config })
  }

  public updateCurrentContacts(data: UserContacts, config: AxiosRequestConfig = {}) {
    const path = 'current/update-contacts/'
    const [url] = this.getUrl(path)
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value) {
        formData.append(key, value)
      }
    })
    return this.request<never, BR<UserContacts>>('POST', url, { data: formData, ...config })
  }

  public autocompleteForGroup(params: unknown, config: AxiosRequestConfig = {}) {
    const path = 'autocomplete/for/group/'
    const [url] = this.getUrl(path)
    return this.request<never, BR<never>>('GET', url, { params, ...config })
  }

  public importPermissions(file: File, config: AxiosRequestConfig = {}) {
    const path = 'import/permissions/'
    const [url] = this.getUrl(path)
    const data = new FormData()
    data.append('file', file)
    return this.request<never, BRC<string>>('POST', url, { data, ...config })
  }

  public importUsers(file: File, config: AxiosRequestConfig = {}) {
    const path = 'import/users/'
    const [url] = this.getUrl(path)
    const data = new FormData()
    data.append('file', file)
    return this.request<never, BRC<string>>('POST', url, { data, ...config })
  }

  public importUsersActivities(file: File, config: AxiosRequestConfig = {}) {
    const path = 'import/activity/'
    const [url] = this.getUrl(path)
    const data = new FormData()
    data.append('file', file)
    return this.request<never, BRC<string>>('POST', url, { data, ...config })
  }

  public permissions(params?: Record<string, unknown>, config = {}) {
    const path = 'current/permissions/'
    const [url] = this.getUrl(path, params)
    const config_n = { params, ...config }
    type IResponse = { data: Data }
    return this.request<null, IResponse>('GET', url, config_n)
  }

  public generateActivityReport(config: AxiosRequestConfig = {}) {
    const path = 'generate/report/activity/'
    const [url] = this.getUrl(path)
    return this.request<never, BR<{ id: number }>>('POST', url, { ...config })
  }
}

export class UsersActionsApi extends BaseApiAuth<UserActionCollection, UserActionCollection> {
  protected readonly endpoint = 'users/:user_id/actions';
}

export class UsersParticipantsApi extends BaseApiAuth<UserParticipantsCollection, UserParticipantsCollection> {
  protected readonly endpoint = 'users/:user_id/participants';
}
